/* =========================================================
   Khotoua — interactions + mesure des conversions
   Port of khotoua-web/assets/js/khotoua.js. Same behaviour, but every
   listener/observer is removed by the returned cleanup function so the
   page can mount/unmount inside React.
   Compatible Google Tag Manager / GA4 (dataLayer), Meta Pixel (fbq), TikTok Pixel (ttq).
   ========================================================= */

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    ttq?: { track?: (...args: unknown[]) => void };
    khotouaTrack?: typeof track;
  }
}

/* ---------- 1. CONFIG : à compléter ---------- */
const CONFIG = {
  playStore: 'https://play.google.com/store/apps/details?id=com.DMNovaComany.Khotoua',
  appStore: '', // ex. https://apps.apple.com/app/id1234567890  (vide = « قريبًا »)
  appGallery: '', // ex. https://appgallery.huawei.com/app/C123456789 (vide = « قريبًا »)
  whatsapp: '21656078078',
  // The lead form posts to its `action` URL (Formgrid, see sections/Contact.tsx).
  // Without an action, it opens WhatsApp with the request already written.
};
const PRICES: Record<string, [string, string]> = {
  tri: ['[السعر الثلاثي] د.ت', '/ الثلاثي'],
  sem: ['[السعر السداسي] د.ت', '/ السداسي'],
  year: ['[السعر السنوي] د.ت', '/ السنة'],
};

type Params = Record<string, string | number>;

/* ---------- 2. Tracking ---------- */
function track(event: string, params: Params = {}) {
  params.page_language = 'ar';
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(Object.assign({ event }, params));
  try { if (typeof window.gtag === 'function') window.gtag('event', event, params); } catch (e) {}
  try {
    if (typeof window.fbq === 'function') {
      if (event === 'app_download_click' || event === 'generate_lead') window.fbq('track', 'Lead', { content_name: event + '_' + (params.store || params.profile || '') });
      else if (event === 'whatsapp_click') window.fbq('track', 'Contact');
      else window.fbq('trackCustom', event, params);
    }
  } catch (e) {}
  try { if (window.ttq && window.ttq.track) window.ttq.track(event === 'app_download_click' ? 'Download' : event, params); } catch (e) {}
}

export function initKhotoua(root: HTMLElement): () => void {
  const cleanups: Array<() => void> = [];
  const on = <K extends keyof HTMLElementEventMap>(el: EventTarget, type: K | string, fn: (e: any) => void, opts?: AddEventListenerOptions) => {
    el.addEventListener(type, fn, opts);
    cleanups.push(() => el.removeEventListener(type, fn, opts));
  };
  const $ = <T extends Element = HTMLElement>(sel: string) => root.querySelector<T>(sel);
  const $$ = <T extends Element = HTMLElement>(sel: string) => Array.from(root.querySelectorAll<T>(sel));

  document.documentElement.classList.add('js');
  cleanups.push(() => document.documentElement.classList.remove('js'));
  window.dataLayer = window.dataLayer || [];
  window.khotouaTrack = track;

  /* ---------- 3. UTM conservés pendant la visite ---------- */
  const KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'ttclid'];
  let utm: Record<string, string> = {};
  try { utm = JSON.parse(sessionStorage.getItem('kh_utm') || '{}'); } catch (e) {}
  const qs = new URLSearchParams(window.location.search);
  KEYS.forEach((k) => { const v = qs.get(k); if (v) utm[k] = v; });
  if (!utm.utm_source && document.referrer && document.referrer.indexOf(window.location.hostname) === -1) {
    try { utm.utm_source = new URL(document.referrer).hostname; utm.utm_medium = 'referral'; } catch (e) {}
  }
  if (!utm.utm_source) { utm.utm_source = 'khotoua.com'; utm.utm_medium = 'website'; }
  try { sessionStorage.setItem('kh_utm', JSON.stringify(utm)); } catch (e) {}

  function placementOf(el: Element) {
    if (el.closest('header')) return 'header';
    if (el.closest('footer')) return 'footer';
    const s = el.closest('section[id]');
    if (s) return s.id === 'top' ? 'hero' : s.id;
    if (el.closest('[aria-label="راسلنا على واتساب"]')) return 'floating';
    return 'page';
  }

  /* Liens Google Play : referrer d'installation (visible dans Play Console › Acquisition) */
  $$<HTMLAnchorElement>('a[href*="play.google.com"]').forEach((a) => {
    const p = placementOf(a);
    const ref = 'utm_source=' + encodeURIComponent(utm.utm_source) + '&utm_medium=' + encodeURIComponent(utm.utm_medium || 'website') +
      '&utm_campaign=' + encodeURIComponent((utm.utm_campaign || 'site') + '_' + p);
    try { const u = new URL(CONFIG.playStore); u.searchParams.set('pcampaignid', 'web_share'); u.searchParams.set('referrer', ref); a.href = u.toString(); } catch (e) {}
    a.setAttribute('data-store', 'android');
  });
  /* App Store / AppGallery : vrais liens si renseignés, sinon « قريبًا » */
  ([['#appstore', CONFIG.appStore, 'ios'], ['#appgallery', CONFIG.appGallery, 'huawei']] as const).forEach(([hash, url, store]) => {
    $$<HTMLAnchorElement>('a[href="' + hash + '"]').forEach((a) => {
      a.setAttribute('data-store', store);
      if (url) { a.href = url; a.target = '_blank'; a.rel = 'noopener'; a.removeAttribute('data-soon'); }
    });
  });

  /* ---------- 4. Toast « قريبا … » ---------- */
  const toast = $('#kh-soon'), toastMsg = $('#kh-soon-msg');
  let toastT: ReturnType<typeof setTimeout> | undefined;
  cleanups.push(() => clearTimeout(toastT));
  function showSoon(msg: string | null) {
    if (!toast) return;
    if (toastMsg && msg) toastMsg.textContent = msg;
    toast.hidden = true; void toast.offsetWidth; toast.hidden = false;
    clearTimeout(toastT);
    toastT = setTimeout(() => { toast.hidden = true; }, 2600);
  }

  /* ---------- 5. Clics : actions + mesure ---------- */
  on(root, 'click', (e: MouseEvent) => {
    const el = (e.target as Element).closest('a,button');
    if (!el) return;
    const placement = placementOf(el);
    const href = el.getAttribute('href') || '';
    const store = el.getAttribute('data-store');

    if (el.hasAttribute('data-soon')) {
      e.preventDefault();
      showSoon(el.getAttribute('data-soon'));
      track(el.getAttribute('data-track') || (store ? 'store_coming_soon_click' : 'coming_soon_click'), { store: store || '', placement });
      return;
    }
    if (store) { track('app_download_click', { store, placement }); return; }
    if (href.indexOf('KHOTOUA_DEMO') > -1) { track('demo_click', { placement }); return; }
    if (href.indexOf('wa.me') > -1) { track('whatsapp_click', { placement }); return; }
    if (href.indexOf('tel:') === 0) { track('contact_click', { method: 'phone' }); return; }
    if (href.indexOf('mailto:') === 0) { track('contact_click', { method: 'email' }); return; }
    const net = href.match(/instagram|facebook|tiktok|linkedin|youtube/);
    if (net) { track('social_click', { network: net[0] }); return; }
    if (href.indexOf('dmnova') > -1) track('outbound_click', { target: 'dmnova' });
  });

  /* ---------- 6. Menu mobile ---------- */
  const burger = $('.kh-burger'), menu = $('#kh-menu');
  if (burger && menu) {
    on(burger, 'click', () => {
      const open = menu.classList.toggle('open');
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    on(menu, 'click', (e: MouseEvent) => { if ((e.target as Element).closest('a')) { menu.classList.remove('open'); burger.setAttribute('aria-expanded', 'false'); } });
  }

  /* ---------- 7. Onglets « مميزات التطبيق » ---------- */
  function tabStyle(isOn: boolean) {
    return 'min-height: 48px; padding: 0 20px; border-radius: 999px; cursor: pointer; font-weight: 700; font-size: 15px; border: 2px solid ' +
      (isOn ? '#141C63' : '#E1E5F7') + '; background: ' + (isOn ? '#141C63' : '#FFFFFF') + '; color: ' + (isOn ? '#FFFFFF' : '#121A4A');
  }
  const tabs = $$('[data-tab]');
  function pickTab(i: number, focus?: boolean) {
    tabs.forEach((t, k) => {
      const isOn = k === i;
      t.setAttribute('aria-selected', isOn ? 'true' : 'false');
      t.setAttribute('tabindex', isOn ? '0' : '-1');
      t.setAttribute('style', tabStyle(isOn));
      if (isOn && focus) t.focus();
    });
    $$('[data-panel]').forEach((p) => { p.hidden = +(p.getAttribute('data-panel') || 0) !== i; });
  }
  tabs.forEach((t, i) => {
    t.setAttribute('tabindex', i === 0 ? '0' : '-1');
    on(t, 'click', () => { pickTab(i); track('feature_tab_select', { tab: (t.textContent || '').trim() }); });
    on(t, 'keydown', (e: KeyboardEvent) => {
      const d = e.key === 'ArrowLeft' ? 1 : e.key === 'ArrowRight' ? -1 : 0; // RTL
      if (d) { e.preventDefault(); pickTab((i + d + tabs.length) % tabs.length, true); }
    });
  });

  /* ---------- 8. Abonnements : ثلاثي / سداسي / سنوي ---------- */
  function pillStyle(isOn: boolean) {
    return 'min-height: 44px; padding: 0 20px; border-radius: 999px; border: 0; cursor: pointer; font-weight: 700; background: ' +
      (isOn ? '#141C63' : 'transparent') + '; color: ' + (isOn ? '#FFFFFF' : '#121A4A');
  }
  const price = $('#kh-price'), period = $('#kh-period');
  const bills = $$('[data-bill]');
  bills.forEach((btn) => {
    on(btn, 'click', () => {
      const k = btn.getAttribute('data-bill') || '';
      bills.forEach((o) => {
        const isOn = o === btn;
        o.setAttribute('style', pillStyle(isOn));
        o.setAttribute('aria-pressed', isOn ? 'true' : 'false');
      });
      if (PRICES[k]) { if (price) price.textContent = PRICES[k][0]; if (period) period.textContent = PRICES[k][1]; }
      track('pricing_toggle', { billing: k });
    });
  });

  /* ---------- 9. Formulaire : وليّ / مدرسة / مؤسّسة ---------- */
  function radioStyle(isOn: boolean) {
    return 'display: inline-flex; align-items: center; gap: 8px; min-height: 44px; padding: 0 16px; border-radius: 999px; border: 2px solid ' +
      (isOn ? '#141C63' : '#E1E5F7') + '; font-weight: 600; cursor: pointer';
  }
  const form = $<HTMLFormElement>('#kh-lead');
  function who() { const r = form && form.querySelector<HTMLInputElement>('input[name="who"]:checked'); return r ? r.value : 'parent'; }
  function syncWho() {
    const w = who();
    $$('[data-who-label]').forEach((l) => { l.setAttribute('style', radioStyle(l.getAttribute('data-who-label') === w)); });
    $$('[data-who-panel]').forEach((p) => { p.hidden = p.getAttribute('data-who-panel') !== w; });
  }
  if (form) {
    form.querySelectorAll('input[name="who"]').forEach((r) => on(r, 'change', syncWho));
    syncWho();
    let started = false;
    on(form, 'input', () => { if (!started) { started = true; track('form_start', { profile: who() }); } });
    const msg = $('#kh-form-msg');
    const say = (text: string, ok: boolean) => {
      if (!msg) return;
      msg.hidden = false; msg.textContent = text;
      msg.style.background = ok ? '#E2F7F3' : '#FDECEC'; msg.style.color = ok ? '#145E55' : '#9B1C1F';
    };
    on(form, 'submit', (e: SubmitEvent) => {
      e.preventDefault();
      const field = (n: string) => form.elements.namedItem(n) as HTMLInputElement | HTMLSelectElement | null;
      const name = field('name') as HTMLInputElement, phone = field('phone') as HTMLInputElement, email = field('email') as HTMLInputElement;
      let bad: HTMLInputElement | null = null;
      [name, phone].forEach((f) => { const v = !f.value.trim(); f.setAttribute('aria-invalid', v ? 'true' : 'false'); if (v && !bad) bad = f; });
      if (email.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) { email.setAttribute('aria-invalid', 'true'); bad = bad || email; } else email.setAttribute('aria-invalid', 'false');
      if (bad) { say('الرجاء إدخال الاسم ورقم الهاتف' + (bad === email ? ' وبريد إلكتروني صحيح' : '') + '.', false); (bad as HTMLInputElement).focus(); return; }
      const w = who();
      const data: Record<string, string> = { profile: w, name: name.value.trim(), phone: phone.value.trim(), email: email.value.trim() };
      const level = field('level'), school = field('school'), organization = field('organization');
      if (w === 'parent' && level) data.school_level = level.value;
      if (w === 'school' && school) data.school_name = school.value.trim();
      if (w === 'partner' && organization) data.organization = organization.value.trim();
      Object.keys(utm).forEach((k) => { data[k] = utm[k]; });
      track('generate_lead', { profile: w, school_level: data.school_level || '', school_name: data.school_name || '', organization: data.organization || '' });

      const endpoint = form.getAttribute('action');
      if (endpoint) {
        const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
        if (btn) btn.disabled = true;
        // Same encoding as a native form POST, so no CORS preflight is needed
        fetch(endpoint, { method: 'POST', headers: { Accept: 'application/json' }, body: new URLSearchParams(data) })
          .then((r) => { if (!r.ok) throw new Error(String(r.status)); say('شكرًا! وصلنا طلبك وسنتّصل بك قريبًا.', true); form.reset(); syncWho(); })
          .catch(() => { say('تعذّر الإرسال. راسلنا على واتساب أو اتّصل بنا على 56 078 078.', false); })
          .then(() => { if (btn) btn.disabled = false; });
      } else {
        const whoAr = ({ parent: 'وليّ', school: 'مدرسة', partner: 'مؤسّسة / شريك' } as Record<string, string>)[w];
        const lines = ['مرحبا خطوة، أطلب عرضًا تجريبيًّا.', 'أنا: ' + whoAr, 'الاسم: ' + data.name, 'الهاتف: ' + data.phone];
        if (data.email) lines.push('البريد: ' + data.email);
        if (data.school_level) lines.push('المستوى: ' + data.school_level);
        if (data.school_name) lines.push('المدرسة: ' + data.school_name);
        if (data.organization) lines.push('المؤسّسة: ' + data.organization);
        window.open('https://wa.me/' + CONFIG.whatsapp + '?text=' + encodeURIComponent(lines.join('\n')), '_blank', 'noopener');
        say('شكرًا! أكمل الإرسال في واتساب وسنردّ عليك قريبًا.', true);
      }
    });
  }

  /* ---------- 10. Flèche « تابع النزول » + profondeur de défilement ---------- */
  const next = $('#kh-next');
  const nextBtn = $('[data-scroll-next]');
  if (nextBtn) on(nextBtn, 'click', () => { window.scrollBy({ top: Math.round(window.innerHeight * 0.85), behavior: 'smooth' }); });
  const marks = [25, 50, 75, 90], hit: Record<number, boolean> = {};
  function onScroll() {
    const d = document.documentElement, y = window.scrollY || d.scrollTop;
    if (next) next.classList.toggle('kh-hide', y + window.innerHeight >= d.scrollHeight - 160);
    const pct = Math.round((y + window.innerHeight) / d.scrollHeight * 100);
    marks.forEach((m) => { if (pct >= m && !hit[m]) { hit[m] = true; track('scroll_depth', { percent: m }); } });
  }
  on(window, 'scroll', onScroll, { passive: true });
  onScroll();

  /* ---------- 11. Vues de sections + apparition douce ---------- */
  if ('IntersectionObserver' in window) {
    const seen: Record<string, boolean> = {};
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting && !seen[en.target.id]) { seen[en.target.id] = true; track('section_view', { section: en.target.id }); }
      });
    }, { threshold: 0.3 });
    $$('section[id], footer[id]').forEach((s) => io.observe(s));
    cleanups.push(() => io.disconnect());

    const targets = $$('#about > div, #contents article, #demo figure, #features > div > div:first-child, #trust article, #trust blockquote, #pricing article, #contact form, #schools article, #pedagogy article');
    const ro = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('kh-in'); ro.unobserve(en.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    targets.forEach((t) => {
      if (t.getBoundingClientRect().top < window.innerHeight) return; // déjà visible : pas d'effet
      t.classList.add('kh-reveal'); ro.observe(t);
    });
    cleanups.push(() => {
      ro.disconnect();
      targets.forEach((t) => t.classList.remove('kh-reveal', 'kh-in'));
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
